import { createFileRoute } from "@tanstack/react-router";
import { BUSINESS } from "@/lib/business";
import { QuoteForm } from "@/components/site/QuoteForm";
import { PhoneButton } from "@/components/site/PhoneButton";
import contactHero from "@/assets/contact-hero.webp";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact Phoenix Plumbing Co. | Call 602-834-1208" },
      { name: "description", content: "Contact Phoenix Plumbing Co. for water heaters, drains, leaks, sewer lines, fixtures, and emergency plumbing in Phoenix, AZ. Call 602-834-1208." },
      { property: "og:title", content: "Contact Phoenix Plumbing Co. | Phoenix Plumber" },
      { property: "og:description", content: "Contact Phoenix Plumbing Co. for water heaters, drains, leaks, sewer lines, fixtures, and emergency plumbing in Phoenix, AZ. Call 602-834-1208." },
      { property: "og:url", content: "https://www.phoenixplumbing.co/contact" },
      { name: "twitter:title", content: "Contact Phoenix Plumbing Co. | Phoenix Plumber" },
      { name: "twitter:description", content: "Contact Phoenix Plumbing Co. for water heaters, drains, leaks, sewer lines, fixtures, and emergency plumbing in Phoenix, AZ. Call 602-834-1208." },
    ],
    links: [{ rel: "canonical", href: "https://www.phoenixplumbing.co/contact" }],
  }),
  component: Contact,
});

function Contact() {
  return (
    <>
      <section className="relative bg-secondary text-secondary-foreground overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-40 bg-cover bg-center" style={{ backgroundImage: `url(${contactHero})` }} />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-secondary via-secondary/80 to-secondary/50" />
        <div className="relative mx-auto max-w-7xl px-4 py-12 grid gap-10 lg:grid-cols-[1fr_1fr]">
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold">Contact Phoenix Plumbing Co.</h1>
            <p className="mt-4 text-secondary-foreground/85 text-lg">
              Call us or request service online. Most calls answered live within 60 seconds.
            </p>
            <div className="mt-6 space-y-2 text-lg">
              <a href={BUSINESS.phoneHref} className="block font-extrabold text-accent text-3xl">{BUSINESS.phone}</a>
              <a href={`mailto:${BUSINESS.email}`} className="block underline">{BUSINESS.email}</a>
              <p>{BUSINESS.address}<br/>{BUSINESS.city}, {BUSINESS.state} {BUSINESS.zip}</p>
            </div>
            <div className="mt-6">
              <h2 className="text-accent font-bold mb-2">Hours</h2>
              <ul className="space-y-1 text-secondary-foreground/90">
                {BUSINESS.hours.map((h) => <li key={h.days}><strong>{h.days}:</strong> {h.time}</li>)}
              </ul>
            </div>
            <PhoneButton size="lg" className="mt-6" />
          </div>
          <QuoteForm />
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 py-12">
        <h2 className="text-2xl font-extrabold text-secondary mb-4">Find us</h2>
        <div className="aspect-video w-full overflow-hidden rounded-xl border border-border">
          <iframe
            title="Phoenix Plumbing Co. on Google Maps"
            src={BUSINESS.mapEmbed}
            loading="lazy"
            className="h-full w-full"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
        <a
          href={`https://www.google.com/maps?cid=${BUSINESS.cid}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-block font-bold text-primary underline"
        >
          Find us on Google →
        </a>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-12 prose prose-lg">
        <h2>What to Include When You Contact Us</h2>
        <ul>
          <li>Your name</li>
          <li>A phone number we can reach you at</li>
          <li>Service address and city</li>
          <li>Type of plumbing issue</li>
          <li>When the issue started</li>
          <li>Photos, if available</li>
          <li>A preferred time for service</li>
        </ul>

        <h2>Plumbing Services We Can Help With</h2>
        <ul>
          <li>Water heater repair and installation</li>
          <li>Drain cleaning</li>
          <li>Leak detection</li>
          <li>Sewer line repair</li>
          <li>Toilet repair and installation</li>
          <li>Faucet and fixture installation</li>
          <li>Garbage disposal repair</li>
          <li>Gas line plumbing</li>
          <li>Water filtration and softeners</li>
          <li>Kitchen and bathroom plumbing</li>
          <li>Backflow prevention and testing</li>
          <li>New construction plumbing</li>
          <li>Emergency plumbing service</li>
        </ul>

        <h2>Emergency Plumbing Calls</h2>
        <p>
          For urgent plumbing problems, call {BUSINESS.phone}. Emergency plumbing calls may be
          accepted by phone outside our regular hours. If it is safe, shut off the main water
          valve before we arrive.
        </p>

        <h2>Service Area</h2>
        <p>
          Phoenix Plumbing Co. serves Phoenix and nearby Valley cities, including Glendale,
          Peoria, Scottsdale, Tempe, Mesa, Chandler, Gilbert, Surprise, and Goodyear.
        </p>
      </section>
    </>
  );
}